/**
 * @owners/shared-types — DTOs shared by web, api and worker.
 * Keep this free of runtime code other than constant lists.
 */

/** Every login has exactly one access level (README "Who uses it"). */
export const ACCESS_LEVELS = [
  "owner",
  "owners_investor",
  "asset_manager",
  "hma_user",
  "special_servicer",
  "receiver",
  "admin",
] as const;
export type AccessLevel = (typeof ACCESS_LEVELS)[number];

/** Levels an Admin can assign in the Admin panel. "admin" is never assignable. */
export const CLIENT_LEVELS = ACCESS_LEVELS.filter((l) => l !== "admin") as Exclude<AccessLevel, "admin">[];

export const LEVEL_LABELS: Record<AccessLevel, string> = {
  owner: "Owner",
  owners_investor: "Owners Investor",
  asset_manager: "Asset Manager",
  hma_user: "HMA User",
  special_servicer: "Special Servicer",
  receiver: "Receiver",
  admin: "Admin",
};

/** Pages in left-menu order (canvas Oct 22). Keys are stable; labels are UI text. */
export const PAGES = [
  "todays_update",
  "flash_reports",
  "awaiting_approval",
  "missing_data",
  "asset_management",
  "reo_documents",
  "compliance",
  "debt_compliance",
  "budget_reforecast",
  "financial_reports",
  "my_reports",
  "receiver_reports",
  "receivership",
  "trends",
  "team_members",
  "leadership",
  "guest_reviews",
  "request_records",
  "settings",
  "dataroom",
  "admin_panel",
] as const;
export type PageKey = (typeof PAGES)[number];

export const PAGE_LABELS: Record<PageKey, string> = {
  todays_update: "Today's Update",
  flash_reports: "Flash Reports",
  awaiting_approval: "Awaiting Approval",
  missing_data: "Missing Data",
  asset_management: "Asset Management",
  reo_documents: "REO Documents",
  compliance: "Compliance",
  debt_compliance: "Debt Compliance",
  budget_reforecast: "Budget + Reforecast",
  financial_reports: "Financial Reports",
  my_reports: "My Reports",
  receiver_reports: "Receiver Reports",
  receivership: "Receivership",
  trends: "Trends",
  team_members: "Team Members",
  leadership: "Leadership",
  guest_reviews: "Guest Reviews",
  request_records: "Request Records",
  settings: "Settings",
  dataroom: "Dataroom",
  admin_panel: "Admin panel",
};

/** Capabilities that aren't pages: actions or data classes gated by level. */
export const CAPABILITIES = [
  "approve_spend",          // approve / decline / question items over the approval limit
  "download_bank_statements", // bank statements + reconciliations (never Owner / Owners Investor)
  "upload_dataroom",        // write files into the Dataroom
  "edit_loans",             // enter / edit loan abstracts and covenants (Debt Compliance)
  "edit_owner_cash",        // enter owner-funded CapEx/PIP and distributions paid (Budget + Reforecast)
  "review_statements",      // approve extracted monthly P&Ls of hotels without Meridian books (V012, D3)
] as const;
export type Capability = (typeof CAPABILITIES)[number];

export interface Me {
  id: string;
  name: string;
  email: string;
  company: string | null;
  /** The user's real level. */
  level: AccessLevel;
  /** Level the UI should render as — differs from `level` only while an Admin previews. */
  effectiveLevel: AccessLevel;
  previewing: boolean;
  clientId: string | null;
  clientName: string | null;
  pages: PageKey[];
  capabilities: Capability[];
}

export interface HotelSummary {
  id: string;
  name: string;
  code: string | null;
  rooms: number | null;
  city: string | null;
  state: string | null;
}

export interface AdminUserRow {
  id: string;
  name: string;
  email: string;
  company: string | null;
  level: AccessLevel;
  hotelIds: string[];
  allHotels: boolean;
  dataroom: boolean;
  active: boolean;
  lastSignInAt: string | null;
  locked: boolean; // admin rows: read-only in the Admin panel
}

export type TicketStatus = "open" | "in_progress" | "resolved";  // SPEC §21
export const TICKET_STATUS_LABELS: Record<TicketStatus, string> = { open: "Open", in_progress: "In progress", resolved: "Resolved" };

export interface TicketRow {
  id: string;
  number: number;
  clientName: string | null;
  userName: string;
  userLevel: AccessLevel;
  topic: string;
  status: TicketStatus;
  createdAt: string;
  updatedAt: string;
  messages: { id: string; authorName: string; fromAdmin: boolean; body: string; createdAt: string }[];
}
